import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import { useSourceData } from '@/features/adp/hooks/useSourceData';
import { useTransformationData } from '@/features/adp/hooks/useTransformationData';
import { useIntegrationData } from '@/features/adp/hooks/useIntegrationData';
import {
  getTransformationLookupDatasourceSelectionQueryKey,
  TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY,
  useRegisteredSourceFilesQuery,
  type TransformationLookupDatasourceSelection,
} from '@/features/adp/queries/useSourceQueries';
import { Transformation } from '@/features/adp/services/common/common.types';
import { SourceFileRegisteredField } from '@/features/adp/services/source/source.types';
import { selectCanvasEdges, selectCanvasNodes } from '@/features/adp/state/canvas/canvasSelectors';
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks';
import { TransformationSheetOverlayData } from '@/features/adp/state/overlaySlice';
import { DataTypes, NUMERIC_DATA_TYPE_SET } from '@/features/adp/types/data.types';
import { Button } from '@/ui/button';
import { Separator } from '@/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { getAllUpstreamSourceNodes, getUpstreamNodeIds } from '../../canvas/canvas.utils';
import { ResolvedIcon } from '../../shared/ResolvedIcon';
import { RenderField, type DynamicDataSources } from './TransformationFields';
import {
  AppliedTransformation,
  applyTransformationBatchesToColumns,
  buildPartialReset,
  buildZodSchema,
  normalizeString,
  normalizeStringArray,
  parseLookupSourceId,
  SELECTED_COLUMNS_WITH_TYPES_KEY,
  toFieldOptions,
  type AppliedTransformationBatch,
  type ColumnSnapshot,
} from './transformationSheet.utils';
import { toast } from 'sonner';

const LOOKUP_DATASOURCE_ID_KEY = 'LookupTable_DataSourceId';

const TransformationSheet = () => {
  const overlayState = useAppSelector(state => state.overlay);
  const { isOpen, type, data, stack } = overlayState;
  const activeFrame = type === 'transformation-sheet'
    ? (data as TransformationSheetOverlayData | null)
    : (stack
      .slice()
      .reverse()
      .find(frame => frame.type === 'transformation-sheet')?.data as TransformationSheetOverlayData | undefined) ?? null;
  const transformationSheetData = activeFrame;
  const isTransformationSheetOpen =
    isOpen && (type === 'transformation-sheet' || stack.some(frame => frame.type === 'transformation-sheet'));
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const transformationData: Transformation[] = queryClient.getQueryData(['sidebar-data'])?.[2];
  const selectedTransformation = transformationData?.find(
    t => t?.id === Number(transformationSheetData?.transformationId)
  );

  const fields = selectedTransformation?.schema_definition?.fields ?? [];
  const transformationName = selectedTransformation?.name ?? 'Transformation';

  // ── Upstream source nodes + their stored data
  const canvasNodes = useAppSelector(selectCanvasNodes);
  const canvasEdges = useAppSelector(selectCanvasEdges);
  const { getSourceDataByNodeId } = useSourceData();

  /**
   * Walk the canvas graph upstream from the current transformation node and
   * collect every source node together with its session-store data.
   * Re-computed only when the node id, canvas nodes, or canvas edges change.
   */
  const upstreamSources = useMemo(() => {
    const nodeId = transformationSheetData?.nodeId;
    if (!nodeId) return [];
    return getAllUpstreamSourceNodes(nodeId, canvasNodes, canvasEdges).map((entry) => ({
      ...entry,
      sourceData: getSourceDataByNodeId(entry.nodeId),
    }));
  }, [transformationSheetData?.nodeId, canvasNodes, canvasEdges, getSourceDataByNodeId]);

  const [appliedList, setAppliedList] = useState<AppliedTransformation[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const previousLookupSourceIdRef = useRef<number | null>(null);
  const { setTransformationData, getTransformationDataByNodeId } = useTransformationData();
  const { getIntegrationDataByNodeId } = useIntegrationData();

  // Abbreviation of the currently open transformation (e.g. "AG", "DP").
  const abbreviation = selectedTransformation?.abbreviation ?? '';

  const zodSchema = buildZodSchema(fields);

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: {},
  });

  const watchedLookupTableName = useWatch({ control: control as never, name: 'LookupTableName' as never });
  const watchedLookupJoinColumn = useWatch({ control: control as never, name: 'LookUpColumns' as never });
  const watchedLookupGetColumns = useWatch({ control: control as never, name: 'LookUpGetColumns' as never });

  const { data: lookupDatasourceSelection } = useQuery<TransformationLookupDatasourceSelection | null>({
    queryKey: TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY,
    queryFn: async () => null,
    initialData: null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const lookupSourceDataSourceId = lookupDatasourceSelection?.sourceDataSourceId ?? null;

  const { data: registeredLookupTables = [] } = useRegisteredSourceFilesQuery(
    lookupSourceDataSourceId,
    undefined,
    { enabled: isTransformationSheetOpen && Boolean(lookupSourceDataSourceId) }
  );

  const lookupTableOptions = useMemo(
    () =>
      registeredLookupTables.map((table) => ({
        label: table.table_name,
        value: table.table_name,
      })),
    [registeredLookupTables]
  );

  const lookupColumnsForSelectedTable = useMemo(() => {
    const selectedLookupTableName =
      typeof watchedLookupTableName === 'string' ? watchedLookupTableName : '';
    const matched = registeredLookupTables.find((table) => table.table_name === selectedLookupTableName);
    if (!matched) return [];
    return matched.fields.map((field) => ({
      label: field.field_name,
      value: field.field_name,
    }));
  }, [registeredLookupTables, watchedLookupTableName]);

  const currentLookupTableValue = normalizeString(watchedLookupTableName);
  const currentLookupJoinColumnValue = normalizeString(watchedLookupJoinColumn);
  const currentLookupGetColumnsValue = useMemo(
    () => normalizeStringArray(watchedLookupGetColumns),
    [watchedLookupGetColumns]
  );

  /**
   * Build live option lists from the first upstream source PLUS any
   * transformations already added in this session.
   *
   * The column options are "replayed" through transformation batches
   * so that, e.g., after adding an Aggregation row the next row's column
   * dropdowns only show the group-by columns + new aggregate columns.
   *
   * When editing an existing item at index N we only replay items 0..N-1 so
   * the form shows the columns that were available at the time it was first
   * filled in.
   */
  const dynamicDataSources = useMemo((): DynamicDataSources => {
    const baseLookupColumnOptions = lookupColumnsForSelectedTable;
    const lookupExtraColumnValues = [
      currentLookupJoinColumnValue,
      ...currentLookupGetColumnsValue,
    ]
      .filter(Boolean)
      .filter((v, idx, arr) => arr.indexOf(v) === idx)
      .filter(v => !baseLookupColumnOptions.some(o => o.value === v));
    const lookupColumnOptions = [
      ...lookupExtraColumnValues.map(v => ({ label: v, value: v })),
      ...baseLookupColumnOptions,
    ];

    const lookupOptionsOnly: DynamicDataSources = {
      tables_based_on_datasource:
        currentLookupTableValue && !lookupTableOptions.some(o => o.value === currentLookupTableValue)
          ? [{ label: currentLookupTableValue, value: currentLookupTableValue }, ...lookupTableOptions]
          : lookupTableOptions,
      columns_based_on_lookup_table: lookupColumnOptions,
    };

    const primary = upstreamSources[0];
    if (!primary?.sourceData) return lookupOptionsOnly;

    const sourceData = primary.sourceData.data;

    // Table name
    const tableName = typeof sourceData.source_selected_table === 'string'
      ? sourceData.source_selected_table
      : '';

    const tableOptions = tableName
      ? [{ label: tableName, value: tableName }]
      : [];

    // Columns from source_files
    const sourceFiles = Array.isArray(sourceData.source_files)
      ? (sourceData.source_files as SourceFileRegisteredField[])
      : [];

    const baseFields: ColumnSnapshot[] = sourceFiles
      .map(field => ({
        fieldName: field.field_name ?? '',
        dataType: typeof field.field_data_type === 'string'
          ? field.field_data_type.toLowerCase()
          : '',
      }))
      .filter(field => field.fieldName.length > 0);

    // 1) Replay upstream transformation nodes stored in session for this
    // canvas path so new nodes inherit previously transformed columns.
    const currentNodeId = transformationSheetData?.nodeId;
    const upstreamIds = currentNodeId ? getUpstreamNodeIds(currentNodeId, canvasEdges) : [];
    const nodeTypeById = new Map(canvasNodes.map(node => [node.id, node.type]));
    // Integration nodes (e.g. Join `JR`, Union `UN`) also persist their config
    // via `useTransformationData` with an abbreviation, so they need to take
    // part in the upstream-batch replay just like regular transformations —
    // otherwise downstream column dropdowns won't include Join's output cols.
    const upstreamTransformationBatches: AppliedTransformationBatch[] = upstreamIds
      .filter(id => {
        const nodeType = nodeTypeById.get(id);
        return nodeType === 'transformation' || nodeType === 'integration';
      })
      .flatMap(id => {
        // Transformations persist under `transformationData`; integrations
        // under `integrationData`. Use the right getter so JR/UN replays
        // pick up their saved config.
        const isIntegration = nodeTypeById.get(id) === 'integration';
        const entry = isIntegration
          ? getIntegrationDataByNodeId(id)
          : getTransformationDataByNodeId(id);
        const abbreviationFromEntry = typeof entry?.data?.abbreviation === 'string'
          ? entry.data.abbreviation
          : '';
        const itemsFromEntry = Array.isArray(entry?.data?.data)
          ? (entry.data.data as AppliedTransformation[])
          : [];
        if (!abbreviationFromEntry || itemsFromEntry.length === 0) return [];
        return [{ abbreviation: abbreviationFromEntry, items: itemsFromEntry }];
      });

    // 2) Replay only the local items BEFORE the one being edited (or all items
    // when adding a new entry).
    const replayUpTo = editingId === null
      ? appliedList.length
      : Math.max(0, appliedList.findIndex(item => item.id === editingId));

    const liveFields = applyTransformationBatchesToColumns(
      baseFields,
      [
        ...upstreamTransformationBatches,
        { abbreviation, items: appliedList.slice(0, replayUpTo) },
      ],
    );

    return {
      tables_list: tableOptions,
      columns_based_on_table: toFieldOptions(liveFields),
      string_columns_based_on_table: toFieldOptions(
        liveFields.filter(f => f.dataType === DataTypes.string)
      ),
      numeric_columns_based_on_table: toFieldOptions(
        liveFields.filter(f => NUMERIC_DATA_TYPE_SET.has(f.dataType))
      ),
      date_columns_based_on_table: toFieldOptions(
        liveFields.filter(f =>
          [DataTypes.date, DataTypes.timestamp].includes(f.dataType as DataTypes)
        )
      ),
      ...lookupOptionsOnly,
    };
  }, [
    upstreamSources,
    appliedList,
    editingId,
    abbreviation,
    transformationSheetData?.nodeId,
    canvasEdges,
    canvasNodes,
    getTransformationDataByNodeId,
    getIntegrationDataByNodeId,
    lookupTableOptions,
    lookupColumnsForSelectedTable,
    currentLookupTableValue,
    currentLookupJoinColumnValue,
    currentLookupGetColumnsValue,
  ]);

  useEffect(() => {
    if (!lookupDatasourceSelection) return;
    setValue('LookupTable_DataSource' as never, lookupDatasourceSelection.dataSourceName as never);
  }, [lookupDatasourceSelection, setValue]);

  useEffect(() => {
    if (!lookupDatasourceSelection?.sourceDataSourceId) {
      previousLookupSourceIdRef.current = null;
      return;
    }

    const nextLookupSourceId = lookupDatasourceSelection.sourceDataSourceId;
    const previousLookupSourceId = previousLookupSourceIdRef.current;
    previousLookupSourceIdRef.current = nextLookupSourceId;

    // Clear dependent fields only when user switches to a different datasource.
    if (previousLookupSourceId === null || previousLookupSourceId === nextLookupSourceId) {
      return;
    }

    const currentValues = getValues();
    reset({
      ...currentValues,
      LookupTableName: '',
      LookUpColumns: '',
      LookUpGetColumns: [],
    });
  }, [lookupDatasourceSelection?.sourceDataSourceId, getValues, reset]);

  useEffect(() => {
    const nodeId = transformationSheetData?.nodeId;
    const stored = nodeId ? getTransformationDataByNodeId(nodeId) : null;

    // Rehydrate applied list from previously saved data for this node.
    if (stored?.data && Array.isArray(stored.data.data)) {
      setAppliedList(stored.data.data as AppliedTransformation[]);
    } else {
      setAppliedList([]);
    }
    reset({});
    setEditingId(null);
    setFormKey(k => k + 1);
    previousLookupSourceIdRef.current = null;
    queryClient.setQueryData(TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY, null);
  }, [transformationSheetData?.transformationId, transformationSheetData?.nodeId]);

  const onAdd = (formData: Record<string, unknown>) => {
    const persistedFormData: Record<string, unknown> = { ...formData };
    if (lookupDatasourceSelection?.sourceDataSourceId) {
      persistedFormData[LOOKUP_DATASOURCE_ID_KEY] = lookupDatasourceSelection.sourceDataSourceId;
      persistedFormData.LookupTable_DataSource = lookupDatasourceSelection.dataSourceName;
    }

    // Enrich selected lookup columns with data type metadata from the API response.
    // We store a generic SelectedColumnsWithTypes array so downstream consumers
    // (e.g. pipeline execution, column mapping) can use type info without re-fetching.
    const selectedGetColumns = normalizeStringArray(formData.LookUpGetColumns);

    if (selectedGetColumns.length > 0) {
      const selectedTableName =
        typeof formData.LookupTableName === 'string' ? formData.LookupTableName : '';
      const matchedTable = registeredLookupTables.find(
        t => t.table_name === selectedTableName
      );
      const fieldTypeMap = new Map(
        (matchedTable?.fields ?? []).map(f => [f.field_name, f.data_type])
      );
      persistedFormData[SELECTED_COLUMNS_WITH_TYPES_KEY] = selectedGetColumns.map(colName => ({
        name: colName,
        dataType: fieldTypeMap.get(colName) ?? '',
      }));
    } else {
      persistedFormData[SELECTED_COLUMNS_WITH_TYPES_KEY] = [];
    }

    if (editingId) {
      setAppliedList(prev =>
        prev.map(item => (item.id === editingId ? { ...item, formData: persistedFormData } : item))
      );
      setEditingId(null);
    } else {
      const newItem: AppliedTransformation = {
        id: crypto.randomUUID(),
        label: `${transformationName} ${appliedList.length + 1}`,
        formData: persistedFormData,
      };
      setAppliedList(prev => [...prev, newItem]);
    }
    reset(buildPartialReset(fields, persistedFormData, dynamicDataSources));
    setFormKey(k => k + 1);
  };

  const handleEdit = (item: AppliedTransformation) => {
    const lookupSourceIdRaw = item.formData[LOOKUP_DATASOURCE_ID_KEY];
    const lookupSourceNameRaw = item.formData.LookupTable_DataSource;
    const lookupSourceId = parseLookupSourceId(lookupSourceIdRaw);
    const lookupSourceName = normalizeString(lookupSourceNameRaw);

    if (Number.isFinite(lookupSourceId) && lookupSourceId > 0 && lookupSourceName) {
      const selectedLookupDatasource: TransformationLookupDatasourceSelection = {
        sourceDataSourceId: lookupSourceId,
        dataSourceName: lookupSourceName,
      };
      // Update the ref BEFORE setting query data so the sourceDataSourceId
      // change effect treats this as "same source" and does NOT clear the
      // dependent fields (LookupTableName, LookUpColumns, LookUpGetColumns)
      // that we are about to restore via reset().
      previousLookupSourceIdRef.current = lookupSourceId;
      queryClient.setQueryData<TransformationLookupDatasourceSelection>(
        TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY,
        selectedLookupDatasource
      );
      queryClient.setQueryData<TransformationLookupDatasourceSelection>(
        getTransformationLookupDatasourceSelectionQueryKey(lookupSourceId),
        selectedLookupDatasource
      );
    }

    setEditingId(item.id);
    reset(item.formData);
  };

  const handleDelete = (id: string) => {
    setAppliedList(prev => {
      const filtered = prev.filter(i => i.id !== id);
      return filtered.map((item, idx) => ({
        ...item,
        label: `${transformationName} ${idx + 1}`,
      }));
    });
    if (editingId === id) {
      setEditingId(null);
      reset(buildPartialReset(fields, getValues(), dynamicDataSources));
      setFormKey(k => k + 1);
    }
  };

  const handleReset = () => {
    reset(buildPartialReset(fields, getValues(), dynamicDataSources));
    setEditingId(null);
    setFormKey(k => k + 1);
  };

  const handleSave = () => {
    const nodeId = transformationSheetData?.nodeId;
    if (!nodeId || !selectedTransformation) return;

    setTransformationData(nodeId, {
      id: selectedTransformation.id,
      name: selectedTransformation.name,
      description: selectedTransformation.description,
      abbreviation: selectedTransformation.abbreviation,
      data: appliedList,
    });

    toast.success(`${selectedTransformation?.name} transformation saved successfully`);

    dispatch(closeOverlayAsync());
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) dispatch(closeOverlayAsync());
  };

  return (
    <Sheet open={isTransformationSheetOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        noBackdrop
        topOffset={64}
        className="shadow-none w-full sm:max-w-[720px] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Transformation Details</SheetTitle>
        </SheetHeader>
        <p className="text-sm px-3 text-muted-foreground">
          Enter the transformation details for the source
        </p>
        <Separator />

        <div className="flex items-center gap-3 px-5 py-2">
          <div className="rounded-md border shadow-sm p-2">
            <ResolvedIcon
              imageLink={selectedTransformation?.image_link}
              alt={selectedTransformation?.name}
              className="h-12 w-12 object-contain"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-sm">{selectedTransformation?.name}</span>
            <span className="text-xs text-muted-foreground">{selectedTransformation?.description}</span>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden px-5 gap-2">
          <div className="w-[60%] flex flex-col border rounded border-border">
            <form
              onSubmit={handleSubmit(onAdd as never)}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div key={formKey} className="flex-1 overflow-y-auto p-4 space-y-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border">
                {fields.map(field => (
                  <RenderField
                    key={field.key}
                    field={field}
                    namePrefix=""
                    control={control as never}
                    register={register}
                    errors={errors as Record<string, unknown>}
                    dynamicDataSources={dynamicDataSources}
                    getValues={getValues}
                  />
                ))}
                {fields.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No fields defined for this transformation.
                  </p>
                )}
              </div>

              <div className="flex gap-2 px-4 py-3 bg-background">
                <Button type="button" variant="outline" onClick={handleReset}>
                  {editingId ? 'Cancel' : 'Clear'}
                </Button>
                <Button type="submit">
                  {editingId ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </div>

          <div className="w-[40%] flex flex-col bg-muted rounded">
            <div className="px-4 py-3 border-b border-border text-center">
              <span className="text-sm font-semibold">Applied Transformations</span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {appliedList.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6 italic">
                  No transformations added yet.
                </p>
              )}
              {appliedList.map(item => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between rounded-md border px-3 py-2 bg-background text-sm transition-all ${editingId === item.id
                    ? 'border-primary ring-1 ring-primary/30'
                    : 'border-border hover:border-border/80'
                    }`}
                >
                  <span className="text-sm font-medium truncate flex-1 mr-2">{item.label}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(item)}
                      title="Edit"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-background">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TransformationSheet;
